import { useCallback, useEffect, useState } from 'react'

import { api } from '../../lib/api'
import { Pager } from '../../lib/Pager'
import { SortTh, sortParam, useSort } from '../../lib/Sort'
import { useAuthedSession } from '../../lib/SessionContext'

/* ============================================================================
   ข้อมูลหลัก → กะงาน · backend: src/routes/adminShifts.js

   สองชั้นเหมือนหน้าวันหยุด: กะหนึ่งใบ (HR_SHIFT_MASTER) มีเวลาทำงาน 7 วัน
   (HR_SHIFT_DETAIL) — จึงไม่ได้ใช้ MasterTable

   ⚠️ ตารางนี้เป็น **แม่แบบ** ที่ใช้ตอนกำหนดกะให้พนักงาน ไม่ได้ผูกกับ HR_EMP_WORK
      โดยตรง · แก้ที่นี่ไม่กระทบประวัติที่ลงไปแล้ว มีผลกับกะที่กำหนดใหม่เท่านั้น

   ⚠️ ไม่มี filter บริษัท เพราะสองตารางนี้ไม่มี CompanyCode (ใช้ร่วมทุกบริษัท)
      ไม่ใช่ลืมใส่
   ============================================================================ */

const BASE = '/api/admin/shifts'
const nf = new Intl.NumberFormat('th-TH')

type Shift = {
  Shift_ID: number
  Shift_Desc: string
  Shift_Type: string | null
  ProcessTA: boolean
  Shift_Start: string | null
  Shift_End: string | null
  offDays: number
}

type Day = {
  DayofWeek: string
  isHoliday: string
  hadBreak: string
  Shift_Start: string | null
  Break_Start: string | null
  Break_End: string | null
  Shift_End: string | null
  OT_Start: string | null
  OT_End: string | null
  Comment: string | null
  missing?: boolean
}

/* ชื่อวันในฐานเป็น '1-Monday' — แปลงเป็นไทยตอนแสดงเท่านั้น
   ห้ามแปลงตอนส่งกลับ ระบบคิดเวลาเดิมอ่านค่าอังกฤษตรง ๆ */
const DAY_TH: Record<string, string> = {
  '1-Monday': 'จันทร์',
  '2-Tuesday': 'อังคาร',
  '3-Wednesday': 'พุธ',
  '4-Thursday': 'พฤหัสบดี',
  '5-Friday': 'ศุกร์',
  '6-Saturday': 'เสาร์',
  '7-Sunday': 'อาทิตย์',
}

const isYes = (v: string) => v === 'YES'
const hhmm = (v: string | null) => v ?? ''

export default function Shifts() {
  const session = useAuthedSession()
  const mayWrite =
    session.kind === 'admin' && (session.role === 'superadmin' || session.role === 'admin')

  const [rows, setRows] = useState<Shift[] | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sort = useSort([], () => setPage(1))

  /** null = ปิด · 'new' = เพิ่มกะ · ตัวเลข = เปิดเวลาทำงานของกะนั้น */
  const [open, setOpen] = useState<number | 'new' | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
      const sp = sortParam(sort.sort)
      if (sp) qs.set('sort', sp)

      const d = await api<{ rows: Shift[]; total: number }>(`${BASE}?${qs}`)
      setRows(d.rows)
      setTotal(d.total)
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, sort.sort])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <>
      <h1>กะงาน</h1>

      {error ? <p className="error">{error}</p> : null}
      {!rows && !error ? <p>กำลังโหลด…</p> : null}

      <div className="emp-toolbar">
        {mayWrite ? (
          <button type="button" onClick={() => setOpen('new')}>
            เพิ่มกะ
          </button>
        ) : null}
      </div>

      {rows ? (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <SortTh label="รหัส" sortKey="id" sort={sort.sort} onToggle={sort.toggle} className="num" />
                <SortTh label="ชื่อกะ" sortKey="desc" sort={sort.sort} onToggle={sort.toggle} />
                <th>เวลาทำงาน</th>
                <th className="num">วันหยุด/สัปดาห์</th>
                <SortTh label="คิดเวลา" sortKey="processTa" sort={sort.sort} onToggle={sort.toggle} />
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.Shift_ID}>
                  <td className="num">{r.Shift_ID}</td>
                  <td>{r.Shift_Desc}</td>
                  {/* เวลาของวันจันทร์เป็นตัวแทน — กะที่เวลาไม่เท่ากันทุกวันต้องเปิดดู */}
                  <td className="nowrap">
                    {r.Shift_Start && r.Shift_End ? `${r.Shift_Start}–${r.Shift_End}` : '—'}
                  </td>
                  <td className="num">{nf.format(r.offDays)}</td>
                  <td>{r.ProcessTA ? 'คิด' : 'ไม่คิด'}</td>
                  <td>
                    <button type="button" onClick={() => setOpen(r.Shift_ID)}>
                      {mayWrite ? 'จัดการเวลา' : 'ดูเวลา'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {rows && rows.length === 0 ? <p>ยังไม่มีกะงาน</p> : null}

      <Pager
        page={page}
        pageSize={pageSize}
        total={total}
        loading={loading}
        onPage={setPage}
        onPageSize={(n) => {
          setPageSize(n)
          setPage(1)
        }}
      />

      {open !== null ? (
        <ShiftDrawer
          shiftId={open === 'new' ? null : open}
          shift={open === 'new' ? null : (rows?.find((r) => r.Shift_ID === open) ?? null)}
          mayWrite={mayWrite}
          onClose={() => setOpen(null)}
          onSaved={() => void load()}
        />
      ) : null}
    </>
  )
}

function ShiftDrawer({
  shiftId,
  shift,
  mayWrite,
  onClose,
  onSaved,
}: {
  shiftId: number | null
  shift: Shift | null
  mayWrite: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const creating = shiftId === null

  const [desc, setDesc] = useState(shift?.Shift_Desc ?? '')
  const [processTa, setProcessTa] = useState(shift?.ProcessTA ?? true)

  const [days, setDays] = useState<Day[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (creating) return
    let alive = true
    api<{ rows: Day[] }>(`${BASE}/${shiftId}/days`)
      .then((d) => alive && setDays(d.rows))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      alive = false
    }
  }, [creating, shiftId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function saveShift(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      const body = JSON.stringify({ shiftDesc: desc, processTa })
      if (creating) {
        await api(`${BASE}`, { method: 'POST', body })
      } else {
        await api(`${BASE}/${shiftId}`, { method: 'PUT', body })
      }
      setError(null)
      onSaved()
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function saveDay(day: Day) {
    setBusy(true)
    try {
      const d = await api<{ row: Day }>(`${BASE}/${shiftId}/days/${encodeURIComponent(day.DayofWeek)}`, {
        method: 'PUT',
        body: JSON.stringify({
          isHoliday: isYes(day.isHoliday),
          /* มีพัก = กรอกเวลาพักมาครบ — ไม่ได้อ่านจาก d.hadBreak เพราะหน้าจอ
             ไม่มีช่องให้ติ๊กแล้ว ให้ช่องเวลาเป็นตัวตัดสินอย่างเดียว */
          hadBreak: Boolean(day.Break_Start && day.Break_End),
          shiftStart: day.Shift_Start,
          shiftEnd: day.Shift_End,
          breakStart: day.Break_Start,
          breakEnd: day.Break_End,
          otStart: day.OT_Start,
          otEnd: day.OT_End,
          comment: day.Comment,
        }),
      })
      setDays((prev) => prev?.map((x) => (x.DayofWeek === day.DayofWeek ? d.row : x)) ?? null)
      setError(null)
      onSaved()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function removeShift() {
    if (!window.confirm(`ลบกะ "${shift?.Shift_Desc}" ?`)) return
    setBusy(true)
    try {
      await api(`${BASE}/${shiftId}`, { method: 'DELETE' })
      onSaved()
      onClose()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  /** แก้ค่าในแถววันหนึ่ง (ยังไม่บันทึก) */
  const patch = (day: string, fields: Partial<Day>) =>
    setDays((prev) => prev?.map((x) => (x.DayofWeek === day ? { ...x, ...fields } : x)) ?? null)

  return (
    <>
      <button type="button" className="drawer-scrim" onClick={onClose} aria-label="ปิดแผง" />

      <aside className="drawer" role="dialog" aria-modal="true" aria-label="กะงาน">
        <header className="drawer-head">
          <div>
            <p className="drawer-title">{creating ? 'เพิ่มกะ' : `กะ ${shift?.Shift_ID ?? ''}`}</p>
            <p className="drawer-sub">
              {creating ? 'ตั้งต้น จ-ศ 08:30-17:30 · แก้เวลารายวันได้หลังบันทึก' : (shift?.Shift_Desc ?? '')}
            </p>
          </div>
          <button type="button" className="drawer-close" onClick={onClose} aria-label="ปิด">
            ✕
          </button>
        </header>

        <div className="drawer-body">
          {error ? <p className="error">{error}</p> : null}

          <form className="emp-form" onSubmit={(e) => void saveShift(e)}>
            <div className="field-grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) 9rem' }}>
              <label>
                ชื่อกะ
                <input
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  maxLength={400}
                  required
                  disabled={!mayWrite}
                />
              </label>
              <label>
                คิดเวลาทำงาน
                <select
                  value={processTa ? 'yes' : 'no'}
                  onChange={(e) => setProcessTa(e.target.value === 'yes')}
                  disabled={!mayWrite}
                >
                  <option value="yes">คิด</option>
                  <option value="no">ไม่คิด</option>
                </select>
              </label>
            </div>

            {mayWrite ? (
              <p>
                <button type="submit" disabled={busy || !desc.trim()}>
                  {creating ? 'บันทึกกะ' : 'บันทึก'}
                </button>{' '}
                {!creating ? (
                  <button type="button" className="ghost" onClick={() => void removeShift()} disabled={busy}>
                    ลบกะ
                  </button>
                ) : null}
              </p>
            ) : null}
          </form>

          {!creating ? (
            <>
              <p className="fieldset-title">เวลาทำงานราย 7 วัน</p>

              {days === null ? (
                <p>กำลังโหลด…</p>
              ) : (
                <div className="table-wrap">
                  <table className="data shift-days">
                    <thead>
                      <tr>
                        <th>วัน</th>
                        <th>สถานะ</th>
                        <th>เข้า</th>
                        <th>ออก</th>
                        <th>พักเริ่ม</th>
                        <th>พักถึง</th>
                        <th>หมายเหตุ</th>
                        {mayWrite ? <th /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {days.map((d) => (
                        <tr key={d.DayofWeek}>
                          <td className="nowrap">{DAY_TH[d.DayofWeek] ?? d.DayofWeek}</td>
                          <td>
                            <select
                              value={isYes(d.isHoliday) ? 'off' : 'work'}
                              onChange={(e) => patch(d.DayofWeek, { isHoliday: e.target.value === 'off' ? 'YES' : 'NO' })}
                              disabled={!mayWrite}
                              aria-label={`สถานะวัน${DAY_TH[d.DayofWeek] ?? d.DayofWeek}`}
                            >
                              <option value="work">ทำงาน</option>
                              <option value="off">หยุด</option>
                            </select>
                          </td>
                          <td>
                            <input
                              type="time"
                              value={hhmm(d.Shift_Start)}
                              onChange={(e) => patch(d.DayofWeek, { Shift_Start: e.target.value || null })}
                              disabled={!mayWrite}
                              aria-label={`เวลาเข้างานวัน${DAY_TH[d.DayofWeek] ?? d.DayofWeek}`}
                            />
                          </td>
                          <td>
                            <input
                              type="time"
                              value={hhmm(d.Shift_End)}
                              onChange={(e) => patch(d.DayofWeek, { Shift_End: e.target.value || null })}
                              disabled={!mayWrite}
                              aria-label={`เวลาออกงานวัน${DAY_TH[d.DayofWeek] ?? d.DayofWeek}`}
                            />
                          </td>
                          {/* ไม่มีปุ่ม "มีพัก/ไม่มีพัก" แยก — กรอกเวลา = มีพัก, เว้นว่าง = ไม่มี
                              (hadBreak คิดจากช่องพวกนี้ตอนบันทึก) สองแหล่งความจริงที่ขัดกันเอง
                              อย่าง "ไม่มีพัก แต่มีเวลาพัก 12:00-13:00" จะเกิดไม่ได้เลย */}
                          <td>
                            <input
                              type="time"
                              value={hhmm(d.Break_Start)}
                              onChange={(e) => patch(d.DayofWeek, { Break_Start: e.target.value || null })}
                              disabled={!mayWrite}
                              aria-label={`เวลาเริ่มพักวัน${DAY_TH[d.DayofWeek] ?? d.DayofWeek}`}
                            />
                          </td>
                          <td>
                            <input
                              type="time"
                              value={hhmm(d.Break_End)}
                              onChange={(e) => patch(d.DayofWeek, { Break_End: e.target.value || null })}
                              disabled={!mayWrite}
                              aria-label={`เวลาเลิกพักวัน${DAY_TH[d.DayofWeek] ?? d.DayofWeek}`}
                            />
                          </td>
                          <td>
                            <input
                              value={d.Comment ?? ''}
                              onChange={(e) => patch(d.DayofWeek, { Comment: e.target.value || null })}
                              maxLength={400}
                              disabled={!mayWrite}
                              aria-label={`หมายเหตุวัน${DAY_TH[d.DayofWeek] ?? d.DayofWeek}`}
                            />
                          </td>
                          {mayWrite ? (
                            <td>
                              <button
                                type="button"
                                className="ghost tiny"
                                onClick={() => void saveDay(d)}
                                disabled={busy}
                              >
                                บันทึก
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : null}
        </div>
      </aside>
    </>
  )
}
